import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { FeatureName, useIsFeatureEnabled } from '@gripp/shared-logic';
import {
  Colors,
  ColumnData,
  EntityAlertListModal,
  GroupsList,
  SelectedGroupRow,
  SelectionStatus,
  SlideLayout,
  ViewMode,
} from '@gripp/shared-ui';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleProp, StyleSheet, Text, View } from 'react-native';
import { ModalButton } from '../../shared/components/modalButton';
import { AssignmentType, GroupAssignmentForm } from './groupAssignmentForm';
import { Groups as GroupsOld } from './groups-old';
import { GroupsFilters } from './groupsFilters';
import { GroupsSelectionPanelMain } from './groupsSelectionPanel';

export const Groups = () => {
  const isCropCircleEnable = useIsFeatureEnabled(
    FeatureName.CropCircleRedesign
  );

  if (isCropCircleEnable) {
    return <GroupsBase />;
  }

  return <GroupsOld />;
};

export const GroupsBase = ({
  rightColumn: rightColumnOverride,
  viewMode: viewModeOverride,
}: {
  rightColumn?: ColumnData;
  viewMode?: ViewMode;
} = {}) => {
  const { t, i18n } = useTranslation();
  const [showAlertListModal, setShowAlertListModal] = useState(false);
  const [selectedGroupIdForAlertList, setSelectedGroupIdForAlertList] =
    useState<string | undefined>(undefined);

  const [rowsSelected, setRowsSelected] = useState<SelectedGroupRow[]>([]);
  const [selectionStatus, setSelectionStatus] =
    useState<SelectionStatus>('idle');

  const rowsSelectedIds = useMemo(
    () => rowsSelected.map((r) => r.id),
    [rowsSelected]
  );
  const hasSelection = rowsSelected.length > 0;

  const clearSelection = useCallback(() => {
    setRowsSelected([]);
    setSelectionStatus('reset');
    rightColumnOverride?.onBackButtonPress?.();
  }, [rightColumnOverride]);

  const getLabel = (labelStyle: StyleProp<any>, label: string) => {
    return <Text style={[labelStyle, styles.cellText]}>{label}</Text>;
  };

  const AssetsButton = (data: any) => {
    return (
      <ModalButton
        label=""
        customButton={
          <View style={[styles.iconContainer]}>
            <MaterialCommunityIcons
              name="qrcode"
              color={Colors.blackText}
              size={16}
              style={styles.icon}
            />
            {getLabel(styles.countText, data.assets)}
          </View>
        }
        modalComponent={({ onComplete }) => {
          return (
            <GroupAssignmentForm
              groupId={data.id}
              title={data.name}
              onComplete={onComplete}
              assignmentType={AssignmentType.Assets}
            />
          );
        }}
        containerStyle={styles.modalContainer}
      />
    );
  };

  const PeopleButton = (data: any) => {
    return (
      <ModalButton
        label=""
        customButton={
          <View style={[styles.iconContainer]}>
            <MaterialCommunityIcons
              name="account-multiple"
              color={Colors.blackText}
              size={16}
              style={styles.icon}
            />
            {getLabel(styles.countText, data.people)}
          </View>
        }
        modalComponent={({ onComplete }) => {
          return (
            <GroupAssignmentForm
              groupId={data.id}
              title={data.name}
              onComplete={onComplete}
              assignmentType={AssignmentType.People}
            />
          );
        }}
        containerStyle={styles.modalContainer}
      />
    );
  };

  const RoutinesButton = (data: any) => {
    return (
      <ModalButton
        label=""
        customButton={
          <View style={[styles.iconContainer]}>
            <MaterialCommunityIcons
              name="format-list-checks"
              color={Colors.blue}
              size={16}
              style={styles.icon}
            />
            {getLabel(styles.countText, data.routines)}
          </View>
        }
        modalComponent={({ onComplete }) => {
          return (
            <GroupAssignmentForm
              groupId={data.id}
              title={data.name}
              onComplete={onComplete}
              assignmentType={AssignmentType.Routines}
            />
          );
        }}
        containerStyle={styles.modalContainer}
      />
    );
  };

  const GroupAlertsButton = (data: any) => {
    return (
      <Pressable
        onPress={() => {
          setSelectedGroupIdForAlertList(data.id);
          setShowAlertListModal(true);
        }}
        style={[styles.iconContainer]}
      >
        <MaterialCommunityIcons
          name="alarm-check"
          color={Colors.blackText}
          size={16}
          style={styles.icon}
        />
        {getLabel(styles.countText, data.groupAlerts)}
      </Pressable>
    );
  };

  const filterComponent = <GroupsFilters />;

  const columnComponent = (
    <View style={styles.groupsContainer}>
      <GroupsList
        assetButton={AssetsButton}
        peopleButton={PeopleButton}
        routinesButton={RoutinesButton}
        groupAlertsButton={GroupAlertsButton}
        onRowsSelectedChange={setRowsSelected}
        selectedRowIds={rowsSelectedIds}
        selectionStatus={selectionStatus}
        onSelectionStatusChange={setSelectionStatus}
      />
      {showAlertListModal && selectedGroupIdForAlertList && (
        <EntityAlertListModal
          visible={showAlertListModal}
          entityType="group"
          entityId={selectedGroupIdForAlertList}
          onClose={() => {
            setShowAlertListModal(false);
            setSelectedGroupIdForAlertList(undefined);
          }}
        />
      )}
    </View>
  );

  const selectionRightColumn = useMemo<ColumnData | undefined>(() => {
    if (!hasSelection) return undefined;

    const count = rowsSelected.length;
    const title =
      count === 1
        ? t('groups.topBar.groupSelected', {
            defaultValue: '1 group selected',
          })
        : t('groups.topBar.groupsSelected', {
            count,
            defaultValue: `${count} groups selected`,
          });
    i18n.addResource(i18n.language, 'translation', title, title);

    return {
      title,
      mainComponent: <GroupsSelectionPanelMain groupIds={rowsSelectedIds} />,
      showBackButton: true,
      onBackButtonPress: clearSelection,
      disableScroll: false,
    };
  }, [hasSelection, rowsSelected, rowsSelectedIds, clearSelection, t, i18n]);

  const resolvedRightColumn = selectionRightColumn ?? rightColumnOverride;
  const resolvedViewMode =
    viewModeOverride ??
    (hasSelection || rightColumnOverride ? ViewMode.DUAL : ViewMode.SINGLE);

  return (
    <SlideLayout
      leftColumn={{
        title: 'groups.menu.title',
        mainComponent: columnComponent,
        headerComponent: filterComponent,
        disableScroll: true,
      }}
      rightColumn={resolvedRightColumn}
      viewMode={resolvedViewMode}
    />
  );
};

const styles = StyleSheet.create({
  filtersContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  groupSearchContainer: {
    width: 390,
    marginRight: 20,
  },
  groupsContainer: {
    flex: 1,
    overflow: 'hidden',
    zIndex: -1,
  },
  modalContainer: {
    width: 640,
    height: '80%',
    minHeight: 450,
    alignSelf: 'center',
    backgroundColor: Colors.primaryGrayBackground,
    borderRadius: 10,
  },
  iconContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  icon: {
    marginRight: 2,
  },
  countText: {
    fontSize: 16,
  },
  cellText: {
    fontSize: 16,
    fontWeight: '500',
    flexWrap: 'nowrap',
  },
  headerButtonsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
});
